import { useEffect, useMemo, useState } from 'react';
import { ArrowUp, Eye, ExternalLink } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { SITE_ORIGIN } from '../lib/seo';
import { MMT_SLOTS, MonumetricAd } from './MonumetricAds';

type AnswerRow = { id: string; name: string; rank: number };
type Sources = {
  source_1?: string | null;
  source_2?: string | null;
  source_3?: string | null;
};

function formatLongDate(dateStr: string) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function sourceEntries(sources: Sources) {
  return [sources.source_1, sources.source_2, sources.source_3]
    .map((raw) => (typeof raw === 'string' ? raw.trim() : ''))
    .filter(Boolean)
    .map((href, i) => {
      const isUrl = /^https?:\/\//i.test(href);
      let label = `Source ${i + 1}`;
      if (isUrl) {
        try {
          label = new URL(href).hostname.replace(/^www\./, '');
        } catch {
          label = `Source ${i + 1}`;
        }
      }
      return { href: isUrl ? href : null, text: isUrl ? label : href, key: `${href}-${i}` };
    });
}

export function ArchiveGuide({
  topicTitle,
  playDate,
  description,
  sources,
  answers,
  path,
  startRevealed,
  onReveal,
  hideHeading,
}: {
  topicTitle: string;
  playDate: string;
  description?: string | null;
  sources: Sources;
  answers: AnswerRow[];
  path: string;
  startRevealed?: boolean;
  onReveal?: () => void;
  hideHeading?: boolean;
}) {
  const [revealed, setRevealed] = useState(Boolean(startRevealed));
  const ranked = useMemo(
    () => [...answers].sort((a, b) => a.rank - b.rank),
    [answers]
  );
  const links = useMemo(() => sourceEntries(sources), [sources]);
  const canonical = `${SITE_ORIGIN}${path}`;
  const summary =
    description?.trim() ||
    `A hand-ranked Top 100 for this topic, ordered from the most obvious names near #1 to the hidden gems near #100.`;
  const seoDescription = `${summary} Read the full ${topicTitle} list, or play the archive puzzle with five guesses.`;

  useEffect(() => {
    setRevealed(Boolean(startRevealed));
  }, [startRevealed, playDate]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: topicTitle,
        name: topicTitle,
        description: seoDescription,
        datePublished: playDate,
        mainEntityOfPage: canonical,
        url: canonical,
        author: { '@type': 'Organization', name: 'Centumth', url: SITE_ORIGIN },
        publisher: { '@type': 'Organization', name: 'Centumth', url: SITE_ORIGIN },
      },
      {
        '@type': 'ItemList',
        name: topicTitle,
        description: seoDescription,
        numberOfItems: ranked.length,
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        itemListElement: ranked.map((row) => ({
          '@type': 'ListItem',
          position: row.rank,
          name: row.name,
        })),
      },
    ],
  };

  const scrollToGame = () => {
    document.getElementById('archive-game')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      document.querySelector<HTMLInputElement>('#archive-game input[type="text"]')?.focus();
    }, 350);
  };

  return (
    <article className={`w-full ${hideHeading ? 'mt-8' : 'mt-16 pt-10 border-t border-border'}`}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {!hideHeading && (
        <>
          <div className="text-[11px] font-black uppercase tracking-[0.25em] text-foreground/40 mb-3">
            Ranked list
          </div>
          <h2 className="text-3xl font-black tracking-tight leading-tight mb-2">{topicTitle}</h2>
          <p className="text-sm text-foreground/50 mb-6">{formatLongDate(playDate)}</p>
        </>
      )}

      <div className="space-y-4 text-[15px] leading-relaxed text-foreground/75 mb-8">
        <p>{summary}</p>
        <p>
          This list was researched and ranked by hand for Centumth. Entries near #1 are the names
          almost everyone thinks of first. Entries near #100 are true, a little obscure, and still
          fair if you think sideways. Sources below are the starting point — the final order can
          include small editorial calls so the daily puzzle stays balanced.
        </p>
        <p>
          You can play this archive puzzle with five guesses, or reveal the complete ranking if you
          came here looking for the list. Revealing locks this puzzle — you won’t be able to play it
          afterward.
        </p>
      </div>

      {links.length > 0 && (
        <section className="mb-8 rounded-2xl border border-border bg-muted/30 px-4 py-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-foreground/45 mb-3 flex items-center gap-1.5">
            <ExternalLink size={12} />
            Sources
          </div>
          <ul className="flex flex-wrap gap-2 mb-3">
            {links.map((link) => (
              <li key={link.key}>
                {link.href ? (
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold bg-background border border-border px-3 py-1.5 rounded-lg hover:bg-muted transition-colors inline-flex items-center gap-1.5"
                  >
                    {link.text}
                    <ExternalLink size={11} className="text-foreground/40" />
                  </a>
                ) : (
                  <span className="text-xs font-semibold bg-background border border-border px-3 py-1.5 rounded-lg">
                    {link.text}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-foreground/45 leading-relaxed">
            Outbound sources help you verify the ranking. The published order is Centumth’s.
          </p>
        </section>
      )}

      <div className="mb-8 flex justify-center [overflow-anchor:none]">
        <MonumetricAd
          slotId={MMT_SLOTS.inContent}
          className="min-h-[250px] md:min-h-[100px] w-full flex items-center justify-center"
        />
      </div>

      <section>
        <h3 className="text-xl font-black tracking-tight mb-4">The full Top 100</h3>

        {!revealed && (
          <div className="sticky top-2 z-20 mb-4 rounded-2xl border border-border bg-background/95 backdrop-blur px-4 py-3 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={scrollToGame}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-foreground text-background px-4 py-2.5 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
            >
              <ArrowUp size={16} />
              Play the game
            </button>
            <button
              type="button"
              onClick={() => {
                setRevealed(true);
                onReveal?.();
              }}
              className="flex-1 inline-flex items-center justify-center gap-2 border border-border px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-muted transition-colors"
            >
              <Eye size={16} />
              Reveal full 100 list
            </button>
          </div>
        )}

        <div className="relative rounded-3xl border border-border overflow-hidden">
          {!revealed && (
            <div
              className="absolute inset-0 z-10 bg-background/25"
              aria-hidden
            />
          )}
          <ol className={`divide-y divide-border ${revealed ? '' : 'select-none blur-[7px]'}`}>
            {ranked.map((row) => (
              <li
                key={row.id}
                value={row.rank}
                className="flex items-baseline gap-3 px-4 py-2.5"
              >
                <span className="shrink-0 w-10 text-right text-xs font-black tabular-nums text-foreground/40">
                  #{row.rank}
                </span>
                <span className="font-semibold leading-snug">{row.name}</span>
              </li>
            ))}
          </ol>
        </div>
        {!revealed && (
          <p className="sr-only">
            The full ranked list is included on this page. Use Reveal full 100 list to read it, or
            play the game above.
          </p>
        )}

        <div className="mt-8 flex justify-center [overflow-anchor:none]">
          <MonumetricAd
            slotId={MMT_SLOTS.bottomLeaderboard}
            className="min-h-[50px] md:min-h-[90px] w-full flex items-center justify-center"
          />
        </div>
      </section>
    </article>
  );
}
